import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	OrderProduct,
	Shopper,
	SetProductWithParentId,
	MatchedProduct,
	SetProduct,
	CurrencyObject,
	PartialWithId,
} from 'common/types';
import { updateSelectedNonSetProductIds } from 'actions';
import { Theme, Grid, alpha } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import ProductItem from './ProductItem';
import { getProductNameWithVariant } from 'common/utils/productUtils';
import * as ViewSelectors from 'selectors/ViewSelectors';
import { getTranslation } from 'common/modules/translations';
import PriceInput from '../PriceInput';
import classNames from 'classnames';
import { updateProductListPrice } from 'common/modules/orders/products';

interface Props {
	product: OrderProduct;
	shopper: Shopper;
	selectedNonSetProductIds: string[];
	selectedSetProducts: SetProductWithParentId[];
	productToSetHighlightFor: MatchedProduct | null;
	editingPrice: boolean;
	editingShopper: boolean;
	currency: CurrencyObject;
	setProductPriceEdit: (data: PartialWithId<OrderProduct>) => void;
}

export const ProductSet = (props: Props) => {
	const {
		product,
		shopper,
		selectedSetProducts,
		selectedNonSetProductIds,
		productToSetHighlightFor,
		editingPrice,
		editingShopper,
		currency,
		setProductPriceEdit,
	} = props;
	const setProducts = product.setProducts;
	const language = useSelector(ViewSelectors.lang);
	const dispatch = useDispatch();
	const classes = useStyles(props);

	const isProductToHighlight = (setIndex: number): boolean =>
		!!productToSetHighlightFor &&
		productToSetHighlightFor.orderProductId === product.id &&
		productToSetHighlightFor.setIndex === setIndex;

	const renderSetProductName = () => (
		<Typography
			variant="subtitle2"
			className={classes.setProductName}
			display="inline"
			onClick={handleClick}
		>
			{getProductNameWithVariant(
				getTranslation(product.name, language),
				product.variant || null,
				language,
				undefined,
			)}
		</Typography>
	);

	const renderSetProducts = (setProducts: SetProduct[]) =>
		setProducts.map((setProduct, index) => (
			<div key={index}>
				<ProductItem
					setProduct={true}
					productId={product.id}
					setIndex={index}
					product={setProduct}
					productToUpdate={product}
					shopper={shopper}
					selectedNonSetProductIds={selectedNonSetProductIds}
					selectedSetProducts={selectedSetProducts}
					highlightProduct={isProductToHighlight(index)}
					editingPrice={editingPrice}
					editingShopper={editingShopper}
					currency={currency}
					setProductPriceEdit={setProductPriceEdit}
				/>
			</div>
		));

	const handleUpdateProductPrice = (updatedAmount: number) => {
		const updatedProduct = updateProductListPrice(updatedAmount, product);
		setProductPriceEdit(updatedProduct.changes);
		return;
	};

	const renderPriceFields = () => {
		return (
			<Grid container spacing={0} alignContent="space-between" alignItems="flex-start">
				<Grid item xs={6} lg={4}>
					{renderSetProductName()}
				</Grid>
				<Grid item xs={6} lg={8} container spacing={1} justifyContent="flex-end">
					<PriceInput
						amount={product.pricing.listPrice}
						updateProductPrice={handleUpdateProductPrice}
						currency={currency}
					/>
				</Grid>
			</Grid>
		);
	};

	const handleClick = () => {
		if (!product.endDateReturned && !editingShopper) {
			const updatedSelectedProducts = selectedNonSetProductIds.filter(
				(p) => p !== props.product.id,
			);
			if (updatedSelectedProducts.length === selectedNonSetProductIds.length) {
				updatedSelectedProducts.push(props.product.id);
			}
			dispatch(updateSelectedNonSetProductIds(updatedSelectedProducts));
		}
	};

	const itemSelected = selectedNonSetProductIds.includes(props.product.id);

	return (
		setProducts && (
			<div
				className={classNames(classes.setContainer, { [classes.selectedProduct]: itemSelected })}
			>
				{editingPrice ? renderPriceFields() : renderSetProductName()}
				{renderSetProducts(setProducts)}
				<Divider />
			</div>
		)
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		setContainer: {
			marginTop: theme.spacing(2),
		},
		selectedProduct: {
			backgroundColor: alpha(theme.palette.primary.main, 0.1),
		},
		setProductName: {
			color: (props: Props) =>
				props.editingShopper ? theme.palette.text.disabled : theme.palette.primary.main,
			cursor: (props: Props) => (props.editingShopper ? 'auto' : 'pointer'),
		},
	}),
);

export default ProductSet;
